import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GuideTip from './GuideTip';
import Chatbox, { AgentChipProps, type PrefilledText } from './Chatbox';
import MessageList, { getTextContent } from './MessageList';
import type { ClawChatMessage } from '@src/stores/useClawWsStore';
import type { GuideTipItem, TemplateNode } from './guideTipConfig';
import useClawWsStore from '@src/stores/useClawWsStore';
import {
  buildClawAgentSessionKey,
  getBaseKeyModule,
  parseClawAgentSessionKey,
  useClawWs,
} from '@src/hooks/useClawWs';
import './content.less';
import { ArrowContinueLineIcon, Toast } from '@kim/ui';
import JoinGroupKimoModal from './JoinGroupKimoModal';
import {
  getGroupClawCountApi,
  getInKimGroupApi,
  postJoinMoltBookGroupApi,
} from '@src/services/walle';
import {
  getKimoInstallCheckApi,
  getKimoInstallCountApi,
  postKimoInstallApi,
} from '@src/services/kimo';
import useUserInfo from '@src/stores/userModel';
import useAgentsStore from '@/stores/useAgentsStore';
import { useShareChat } from '@src/hooks/useShareChat';
import NewChat from '@/pages/pc/claw/new-chat';
import { CreateThreadParams } from '@/pages/pc/claw/new-chat/hooks/useMessageModel';
import {
  buildMainagentPath,
  MAINAGENT_ROUTE_BASE,
} from '@src/utils/mainagent-route';
import { message } from 'antd';
import { copyTextWithFallback } from '@/utils/clipboard-copy';

//staging: 6728735187151122
//prod: 6737257463915091
const JOINGROUP_KIM_GROUP_ID = '6737257463915091';
const PLACEHOLDER = '今天有什么我可以帮你的？（输入 @ 引用内容）';
const LEGACY_CLAW_DRAFT_STORAGE_KEY = 'claw-chat-drafts-v1';
const WB_EMPLOYEE_CODES = [3, 12, 13, 14, 18, 20, 21];

/** 与 useClawWsStore 中 getSessionKey 一致，用于判断「当前会话」是否仍有未完成回复 */
const sessionKeyBase = (sessionKey: string) =>
  sessionKey.split(':').pop() ?? '';

const SHARE_BAR_HEIGHT = 73;

interface ChatContentProps {
  shareMode?: boolean;
  onExitShare?: () => void;
}

export const ChatContent = ({ shareMode, onExitShare }: ChatContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeInitialPrompt = (
    (location.state as { initialPrompt?: string } | null)?.initialPrompt ?? ''
  ).trim();

  const [activeTemplate, setActiveTemplate] = useState<TemplateNode[] | null>(
    null,
  );
  const [activeSuggestionItem, setActiveSuggestionItem] =
    useState<GuideTipItem | null>(null);
  const [prefilledText, setPrefilledText] = useState<PrefilledText | null>(
    null,
  );
  const [clearEditorNonce, setClearEditorNonce] = useState(0);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [lastAppliedSignature, setLastAppliedSignature] = useState<
    string | null
  >(null);
  const consumedRoutePromptKeyRef = useRef<string | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const chatboxWrapperRef = useRef<HTMLDivElement>(null);
  const [chatboxWrapperHeight, setChatboxWrapperHeight] = useState(156);
  const [placeholder, setPlaceholder] = useState(PLACEHOLDER);
  const [joinGroupKimoModalVisible, setJoinGroupKimoModalVisible] =
    useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allDisplayedIds, setAllDisplayedIds] = useState<string[]>([]);
  const [allDisplayedMsgs, setAllDisplayedMsgs] = useState<ClawChatMessage[]>(
    [],
  );
  const { sharing, share } = useShareChat();
  const [pendingJoinGroupItem, setPendingJoinGroupItem] =
    useState<GuideTipItem | null>(null);
  const [isInJoingroup, setIsInJoingroup] = useState(false);
  const [kimoSkillStatus, setKimoSkillStatus] = useState(false);
  const [kimoJoiningLoading, setKimoJoiningLoading] = useState(false);
  const [groupBotNumber, setGroupBotNumber] = useState<number>(0);
  const [kimoBotNumber, setKimoBotNumber] = useState<number>(0);
  const { currentSessionKey } = useClawWsStore();
  const messages = useClawWsStore((s) => s.messages);
  const userInfo = useUserInfo((s) => s.userInfo);
  const abortStreaming = useClawWsStore((s) => s.abortStreaming);
  const { sendMessage, abortChat, switchSession } = useClawWs();
  const hasSession = !!currentSessionKey;
  const employeeCode = Number((userInfo as any)?.employee_code);

  /** 仅当前会话存在未完成 assistant 时为 true；与 await sendMessage 解耦，避免切走后仍被上一会话的 Promise 卡住无法发送 */
  const isAssistantStreaming = useMemo(() => {
    if (!currentSessionKey) return false;
    const base = sessionKeyBase(currentSessionKey);
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      if (sessionKeyBase(m.sessionKey) !== base) continue;
      return !m.done;
    }
    return false;
  }, [messages, currentSessionKey]);

  const isWbOrFl =
    (userInfo as any)?.front_line === true ||
    WB_EMPLOYEE_CODES.includes(employeeCode);

  // 不再按会话保留输入草稿：清理历史遗留的本地草稿
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CLAW_DRAFT_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // 进入新建页面（session 被清空）时重置输入框状态
  useEffect(() => {
    if (!currentSessionKey) {
      setActiveTemplate(null);
      setActiveSuggestionItem(null);
      setPrefilledText(null);
      setClearEditorNonce((n) => n + 1);
      setPlaceholder(PLACEHOLDER);
      setLastAppliedSignature(null);
    }
  }, [currentSessionKey]);

  // 从创建页携带 initialPrompt 跳转到 /mainagent 时，预填输入框（不自动发送）
  useEffect(() => {
    if (!routeInitialPrompt) return;
    if (consumedRoutePromptKeyRef.current === location.key) return;
    if (currentSessionKey) return;

    consumedRoutePromptKeyRef.current = location.key;

    setActiveSuggestionItem(null);
    setActiveTemplate(null);
    setPrefilledText((prev) => ({
      text: routeInitialPrompt,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
    setPlaceholder('问你想问的知识...');
    setLastAppliedSignature(`route:${routeInitialPrompt}`);

    navigate(location.pathname, { replace: true, state: null });
  }, [
    currentSessionKey,
    location.key,
    location.pathname,
    navigate,
    routeInitialPrompt,
  ]);

  // 监听 chatbox-wrapper 高度，动态调整消息列表底部留白
  useEffect(() => {
    const el = chatboxWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setChatboxWrapperHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 点击面板外部时关闭 suggestion 列表
  useEffect(() => {
    if (!activeSuggestionItem) return;
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node)
      ) {
        setActiveSuggestionItem(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeSuggestionItem]);

  // 进入组件 10s 后检测 WS 连接状态，未连接则 toast 提示
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (useClawWsStore.getState().connStatus !== 'connected') {
        Toast({
          message: '连接未就绪，请刷新页面或稍后重试',
          type: 'warning',
        });
      }
    }, 5_000);
    return () => window.clearTimeout(timer);
  }, []);

  const { fetchAgentList, agentList } = useAgentsStore();
  useEffect(() => {
    if (agentList == null || agentList.length === 0) {
      fetchAgentList();
    }
  }, []);
  // 退出分享模式时清空选中状态
  useEffect(() => {
    if (!shareMode) setSelectedIds(new Set());
  }, [shareMode]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isAllSelected =
    allDisplayedIds.length > 0 && selectedIds.size === allDisplayedIds.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allDisplayedIds));
    }
  };

  const handleConfirmShare = async () => {
    if (selectedIds.size === 0) {
      Toast({ message: '请先选择要分享的消息', type: 'warning' });
      return;
    }

    const selectedMsgs = allDisplayedMsgs.filter((_, idx) =>
      selectedIds.has(allDisplayedIds[idx]!),
    );
    const messagePayloads: string[] = [];
    for (const msg of selectedMsgs) {
      const text = getTextContent(msg.content);
      if (!text) continue;
      messagePayloads.push(JSON.stringify({ role: msg.role, content: text }));
    }

    if (messagePayloads.length === 0) {
      Toast({ message: '所选消息没有可分享的内容', type: 'warning' });
      return;
    }

    const { agentId } = parseClawAgentSessionKey(currentSessionKey ?? '');
    try {
      const shareId = await share({
        marioAgentId: agentId ?? undefined,
        sessionKey: sessionKeyBase(currentSessionKey ?? ''),
        messages: messagePayloads,
      });
      const shareUrl = `${window.location.origin}/message-share/${shareId}`;
      const copyResult = await copyTextWithFallback(shareUrl);
      Toast({
        message: copyResult.success
          ? '分享链接已生成，快去分享吧'
          : `分享链接已生成`,
        type: 'success',
        buttonText: copyResult.success ? undefined : '请点击复制',
        buttonClick: () => copyTextWithFallback(shareUrl),
      });
      onExitShare?.();
    } catch {
      Toast({ message: '分享失败，请重试', type: 'error' });
    }
  };

  const handleClearSession = useCallback(() => {
    switchSession('');
    navigate(MAINAGENT_ROUTE_BASE, { state: {} });
  }, [switchSession, navigate]);
  /** 新建频道且有 数字员工 时为 true */
  const isNewChannelWithAgent =
    !!(location.state?.marioAgentId && location.state?.displayName) &&
    !currentSessionKey;
  const existsAgentChip = useMemo(() => {
    console.log(currentSessionKey, 'currentSessionKey');
    if (!currentSessionKey) {
      return;
    }
    const { agentId, sessionBaseKey } =
      parseClawAgentSessionKey(currentSessionKey);
    console.log(agentId, sessionBaseKey, 'agentId, sessionBaseKey');
    if (agentId && agentId !== 'main' && sessionBaseKey) {
      return {
        name:
          agentList.find((item) => item.marioAgentId === agentId)
            ?.displayName ?? '',
        avatarUrl: agentList.find((item) => item.marioAgentId === agentId)
          ?.icon,
        onRemove: handleClearSession,
      } satisfies AgentChipProps;
    }
  }, [currentSessionKey, agentList]);
  const agentChip =
    existsAgentChip ??
    (isNewChannelWithAgent
      ? ({
          name: location.state?.displayName,
          avatarUrl: location.state?.icon,
          onRemove: handleClearSession,
        } satisfies AgentChipProps)
      : undefined);

  const handleSubmit = async (msg: string) => {
    if (!msg.trim() || isAssistantStreaming) return;
    if (isNewChannelWithAgent) {
      await sendMessage(
        msg,
        undefined,
        undefined,
        undefined,
        buildClawAgentSessionKey(location.state?.marioAgentId),
      );
      return;
    }
    await sendMessage(msg, currentSessionKey ?? undefined);
  };

  // 已进群后再次点击 joingroup 时防抖，避免连续点击多次发送
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const debouncedSubmitJoingroup = useMemo(
    () =>
      debounce((text: string) => {
        handleSubmitRef.current(text);
      }, 600),
    [],
  );

  const applyTemplate = (item: GuideTipItem) => {
    const signature = [
      item.id,
      item.type ?? 'none',
      item.prompt ?? '',
      item.placeholder ?? '',
    ].join(':');
    if (signature === lastAppliedSignature && !isEditorEmpty) return;

    setActiveSuggestionItem(null);
    setActiveTemplate(null);
    setPrefilledText(null);
    setClearEditorNonce((n) => n + 1);
    setPlaceholder(PLACEHOLDER);
    if (item.type === 'template') {
      setActiveTemplate(item.template ? [...item.template] : null);
    }
    if (item.type === 'placeholder') {
      setPlaceholder(item.placeholder ?? '');
    }
    setLastAppliedSignature(signature);
  };

  const handleGuideTipSelect = async (item: GuideTipItem) => {
    if (item.type === 'suggestion' && item.suggestions?.length) {
      setActiveTemplate(null);
      setPrefilledText(null);
      setClearEditorNonce((n) => n + 1);
      setPlaceholder(PLACEHOLDER);
      setActiveSuggestionItem(item);
      return;
    }

    if (item.type === 'prompt') {
      handleSubmit(item.prompt ?? '');
      return;
    }

    // 特殊处理 joingroup：统一先弹窗，再按是否在群里决定按钮行为
    if (item.id === 'joingroup') {
      const username = userInfo?.username;
      let inGroup = false;

      // 优先通过后端判断是否已经在 Kim 群中
      if (JOINGROUP_KIM_GROUP_ID && username) {
        try {
          const res = await getInKimGroupApi({
            groupId: JOINGROUP_KIM_GROUP_ID,
            username,
          });
          inGroup = res.data === true;
        } catch {
          // 接口异常时，默认按未加入处理
          inGroup = false;
        }
      }

      // 弹窗前获取群内 Claw 数量（失败不阻断）
      if (JOINGROUP_KIM_GROUP_ID) {
        try {
          const { data: groupCount } = await getGroupClawCountApi({
            groupId: JOINGROUP_KIM_GROUP_ID,
          });
          if (typeof groupCount === 'number') {
            setGroupBotNumber(groupCount);
          }
        } catch {
          // 忽略统计接口错误，不影响弹窗展示
        }
      }

      setIsInJoingroup(inGroup);

      if (!isWbOrFl) {
        // 弹窗前查询 Kimo skill 状态与 Kimo 广场安装数
        try {
          const skillStatus = await getKimoInstallCheckApi();
          setKimoSkillStatus(skillStatus);
        } catch {
          setKimoSkillStatus(false);
        }
        try {
          const kimoCount = await getKimoInstallCountApi();
          if (typeof kimoCount === 'number') {
            setKimoBotNumber(kimoCount);
          }
        } catch {
          // 忽略统计接口错误，不影响弹窗展示
        }
      }

      setPendingJoinGroupItem(item);
      setJoinGroupKimoModalVisible(true);
      return;
    }

    applyTemplate(item);
  };

  const handleJoinGroupConfirm = async () => {
    if (!pendingJoinGroupItem) return;

    if (isInJoingroup) {
      // 已在群里：查看群聊（发送 joingroup 的 template 文案）
      const templateText =
        pendingJoinGroupItem.template?.find((n) => n.type === 'text')
          ?.content ?? pendingJoinGroupItem.label;
      setJoinGroupKimoModalVisible(false);
      setPendingJoinGroupItem(null);
      debouncedSubmitJoingroup(templateText);
      window.getSelection()?.removeAllRanges();
      return;
    }

    try {
      const res = await postJoinMoltBookGroupApi();
      if (res.code !== 0) {
        Toast({
          message: '尊敬的观察员，当前聊天室 🦞 名额已满，请等待下一批开放～',
          type: 'warning',
        });
        return;
      }
    } catch {
      Toast({
        message: '加入群聊失败，请稍后重试',
        type: 'error',
      });
      return;
    }

    setJoinGroupKimoModalVisible(false);
    setIsInJoingroup(true);
    // 第一次点击弹窗确认后：发送固定文案
    handleSubmit('我们已经加入了 Claw 聊天室，你可以在里面做什么？');
    setPendingJoinGroupItem(null);
  };

  const handleJoinKimoConfirm = async () => {
    if (!pendingJoinGroupItem) return;

    const kimoQuery =
      pendingJoinGroupItem.template?.[1]?.type === 'text'
        ? pendingJoinGroupItem.template[1].content
        : '帮我看看 Kimobook 社区内大家在聊什么';

    if (kimoSkillStatus) {
      // 已加入：直接查看社区
      setJoinGroupKimoModalVisible(false);
      setPendingJoinGroupItem(null);
      handleSubmit(kimoQuery);
      return;
    }

    // 未加入：调用安装接口，返回 true 才继续后续逻辑
    setKimoJoiningLoading(true);
    try {
      const installResult = await postKimoInstallApi();
      if (installResult !== true) {
        throw new Error('install failed');
      }
    } catch {
      Toast({
        message: '加入 Kimobook 社区失败，请稍后重试',
        type: 'error',
      });
      setKimoJoiningLoading(false);
      return;
    }
    setKimoJoiningLoading(false);
    setKimoSkillStatus(true);
    setJoinGroupKimoModalVisible(false);
    setPendingJoinGroupItem(null);
    handleSubmit('我们已经加入了 Kimobook 社区了，你可以在里面做什么？');
  };

  const handleSuggestionClick = (text: string) => {
    const signature = `suggestion:${text}`;
    if (signature === lastAppliedSignature && !isEditorEmpty) {
      return;
    }

    setActiveSuggestionItem(null);
    setActiveTemplate(null);
    setPrefilledText((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1 }));
    setLastAppliedSignature(signature);
  };

  // 切换会话时清空输入（不保留、不恢复草稿）
  useEffect(() => {
    setActiveTemplate(null);
    setActiveSuggestionItem(null);
    setPrefilledText(null);
    setClearEditorNonce((n) => n + 1);
  }, [currentSessionKey]);

  return (
    <div className="claw-chat-content">
      <div
        className={`claw-chat-content-wrapper${hasSession ? ' claw-chat-content-wrapper--session' : ''}`}
        style={
          {
            '--chatbox-h': `${shareMode ? SHARE_BAR_HEIGHT : chatboxWrapperHeight}px`,
          } as React.CSSProperties
        }
      >
        {hasSession ? (
          <>
            <MessageList
              shareMode={shareMode}
              selectedIds={selectedIds}
              onToggle={handleToggleSelect}
              onIdsChange={setAllDisplayedIds}
              onMessagesChange={setAllDisplayedMsgs}
            />
            {sharing && (
              <div className="claw-share-loading">
                <div className="claw-share-loading__spinner" />
              </div>
            )}
          </>
        ) : (
          <GuideTip onSelectItem={handleGuideTipSelect} />
        )}

        <div
          ref={chatboxWrapperRef}
          className="claw-chat-content-chatbox-wrapper"
          style={shareMode ? { display: 'none' } : undefined}
        >
          {activeSuggestionItem?.suggestions?.length && (
            <div ref={suggestionsRef} className="claw-chat-suggestions">
              {activeSuggestionItem.suggestions.map((item, i) => (
                <button
                  key={i}
                  className="claw-chat-suggestions-item"
                  onClick={() => handleSuggestionClick(item.value)}
                  type="button"
                >
                  <ArrowContinueLineIcon
                    className="claw-chat-suggestions-item-icon"
                    aria-hidden
                    size={16}
                  />
                  <span className="claw-chat-suggestions-item-text">
                    {item.content}
                  </span>
                </button>
              ))}
            </div>
          )}
          <Chatbox
            agentChip={agentChip}
            template={activeTemplate ?? undefined}
            prefilledText={prefilledText ?? undefined}
            clearEditorNonce={clearEditorNonce}
            onEditorEmptyChange={setIsEditorEmpty}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            disabled={false}
            isResponding={isAssistantStreaming}
            className="claw-chat-content-chatbox"
            sessionKey={currentSessionKey ?? undefined}
            onClearTemplate={() => setActiveTemplate(null)}
            onAbort={
              currentSessionKey
                ? () => {
                    abortChat(currentSessionKey)
                      .then(() => {
                        abortStreaming(currentSessionKey);
                      })
                      .finally(() => {
                        abortStreaming(currentSessionKey);
                      });
                  }
                : undefined
            }
          />
        </div>

        {shareMode && (
          <div className="claw-share-bar">
            <div className="claw-share-bar__inner">
              <div className="claw-share-bar__left" onClick={handleSelectAll}>
                <span
                  className={`msg-select-radio${isAllSelected ? ' msg-select-radio--checked' : ''}`}
                >
                  {isAllSelected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 3.5L3.8 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="claw-share-bar__label">全选</span>
              </div>
              <div className="claw-share-bar__right">
                <button
                  type="button"
                  className="claw-share-bar__btn claw-share-bar__btn--cancel"
                  onClick={onExitShare}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="claw-share-bar__btn claw-share-bar__btn--confirm"
                  onClick={handleConfirmShare}
                  disabled={sharing}
                >
                  {sharing ? (
                    <span className="claw-share-btn-spinner" />
                  ) : (
                    '分享'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <JoinGroupKimoModal
        isWbOrFl={isWbOrFl}
        groupBotNumber={groupBotNumber}
        kimoBotNumber={kimoBotNumber}
        kimoLoading={kimoJoiningLoading}
        visible={joinGroupKimoModalVisible}
        joinGroupText={isInJoingroup ? '已加入 查看群聊' : '立即加入群聊'}
        joinKimoText={kimoSkillStatus ? '已加入 查看社区' : '立即加入 Kimobook'}
        onClose={() => {
          setJoinGroupKimoModalVisible(false);
          setPendingJoinGroupItem(null);
        }}
        onJoinGroup={handleJoinGroupConfirm}
        onJoinKimo={handleJoinKimoConfirm}
      />
    </div>
  );
};

const ChatWrapper = (props: ChatContentProps) => {
  const navigate = useNavigate();
  const { sessionKey } = useParams<{ sessionKey?: string }>();
  const { currentSessionKey, sessions, setSessions } = useClawWsStore();
  const { switchSession } = useClawWs();
  const location = useLocation();
  const { fetchAgentList, agentList } = useAgentsStore();
  useEffect(() => {
    if (agentList == null || agentList.length === 0) {
      fetchAgentList();
    }
  }, []);

  const handleClearSession = useCallback(() => {
    switchSession('');
    navigate(MAINAGENT_ROUTE_BASE, { state: {} });
  }, [switchSession, navigate]);

  /** 仅随 key 集合变化而变（排序后拼接），sessions 引用频繁变但 key 不变时不触发下游 memo */
  const sessionKeysSig = useMemo(() => {
    const keys = sessions
      .map((s) => s.key)
      .filter((k): k is string => Boolean(k));
    keys.sort();
    return keys.join('\x1e');
  }, [sessions]);

  type ParsedSessionKey = ReturnType<typeof parseClawAgentSessionKey>;

  /** URL 段与 claw/index 一致：用 getBaseKeyModule(fullKey) 做索引 */
  const sessionKeyByUrlBase = useMemo(() => {
    const map = new Map<string, ParsedSessionKey>();
    if (!sessionKeysSig) return map;
    for (const fullKey of sessionKeysSig.split('\x1e')) {
      if (!fullKey) continue;
      const urlBase = getBaseKeyModule(fullKey);
      if (!urlBase) continue;
      map.set(urlBase, parseClawAgentSessionKey(fullKey));
    }
    return map;
  }, [sessionKeysSig]);

  const sessionInfo = useMemo(() => {
    let agentId: string | undefined;
    let sessionBaseKey: string | undefined;

    if (currentSessionKey) {
      const parsed = parseClawAgentSessionKey(currentSessionKey);
      agentId = parsed.agentId;
      sessionBaseKey = parsed.sessionBaseKey;
    } else if (sessionKey) {
      const parsed = sessionKeyByUrlBase.get(sessionKey);
      if (parsed?.agentId && parsed.agentId !== 'main') {
        agentId = parsed.agentId;
        sessionBaseKey = parsed.sessionBaseKey;
      }
    }

    const finalAgentId = agentId || location.state?.marioAgentId;

    const agent = agentList.find((item) => item.marioAgentId === finalAgentId);

    const resolvedAgentType =
      agent?.agentType ||
      (sessionBaseKey?.startsWith('mario-webchat')
        ? 'mario-agent'
        : 'mario-claw');

    return {
      agentId: finalAgentId,
      sessionKey: sessionBaseKey,
      agentType: resolvedAgentType,
      name: agent?.displayName,
      avatarUrl: agent?.icon,
      sendDisabled: !agent,
      onRemove: handleClearSession,
    };
  }, [
    currentSessionKey,
    sessionKey,
    sessionKeyByUrlBase,
    agentList,
    location.state?.marioAgentId,
    handleClearSession,
  ]);

  // 按 URL 恢复 currentSessionKey 只放在 claw/index（避免与 ChatWrapper 双重 switchSession）。
  // 此处仅用 sessionKey + sessionKeyByUrlBase 驱动 sessionInfo，在 store 尚未同步时也能展示 NewChat / chip。

  const handleCreateThread = useCallback(
    (params: CreateThreadParams) => {
      const { content, threadId, marioAgentId, timestamp } = params;
      const key = buildClawAgentSessionKey(marioAgentId, threadId);
      const newSessions = [
        {
          key,
          label: content || '新会话',
          createdAt: Number(timestamp),
          updatedAt: Number(timestamp),
        },
        ...useClawWsStore.getState().sessions,
      ];
      setSessions(newSessions);
      switchSession(key);
      navigate(buildMainagentPath(threadId));
    },
    [switchSession, navigate, setSessions],
  );
  const handleChatBoxClick = useCallback(() => {
    if (sessionInfo?.sendDisabled) {
      message.open({
        type: 'error',
        content: '无聊天权限',
        key: 'no-chat-permission',
      });
    }
  }, [sessionInfo]);

  if (sessionInfo && sessionInfo?.agentType === 'mario-agent') {
    return (
      <NewChat
        agentChip={sessionInfo as any}
        agentId={sessionInfo?.agentId ?? ''}
        disabled={sessionInfo?.sendDisabled}
        onChatBoxClick={handleChatBoxClick}
        onCreateThread={handleCreateThread}
        shareMode={props.shareMode}
        onExitShare={props.onExitShare}
      />
    );
  }

  return <ChatContent {...props} />;
};

export default ChatWrapper;
